import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/esm/Container";
import { jobLookup } from "../../store/thunks/fetch-jobs-thunk";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { fetchProfileStatus } from "../../store/thunks/fetch-profile-status-thunk";
import { fetchProfileEmploymentStatus } from "../../store/thunks/fetch-profile-employment-status-thunk";
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaShareFromSquare } from "react-icons/fa6";
import { LuCopyCheck, LuCopyPlus } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import {
    setJobData,
    resetJobData
} from "../../store/slices/job-data-slice";
import { selectSignIn } from "../../store/slices/signed-in-slice";
import { selectProfileStatus } from "../../store/slices/profile-status-slice";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { selectLastSelectedJob } from "../../store/slices/last-selected-job-slice";
import { setLastSelectedJob, resetLastSelectedJob } from "../../store/slices/last-selected-job-slice";
import { asyncLocalStorage } from "../utils/async-localstorage-helpers";

/**
 * Component that represents the detailed job view which houses more information than the Card view.
 * Component renders based on URL and checks if the URL matches a job in the jobLookup hashmap.
 * @returns
 */
export const  JobDetail = ({ mobileView }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // const selectedJob = useSelector(selectedJobData);

    const lastSelectedJob = useSelector(selectLastSelectedJob)
    const isSignedIn = useSelector(selectSignIn);
    const profileComplete = useSelector(selectProfileStatus);


    // Get the URL parameter.
    let { jobID } = useParams();

    const [copied, setCopied] = useState(false);

    // Choose what to scroll to when loading in to the page
    const ref = useRef(null);

    // Extracts pathname property(key) from an object
    const { pathname } = useLocation();

    // on clicking back on mobile, update the job list and last selected job.
    const goBack = () => {
        dispatch(resetLastSelectedJob()) 
        dispatch(resetJobData())
    }

    // Automatically scrolls to top whenever pathname changes
    useEffect(() => {
        ref.current?.scrollIntoView({ behavior: "smooth" });
        if (copied) {
            setCopied(false);
        }
    }, [pathname]);

    // On page load,
    // Get the status of applicant profile, whether or not the profile is complete with required fields.
    useEffect(() => {
        if(isSignedIn){
            dispatch(fetchProfileStatus());
            dispatch(fetchProfileEmploymentStatus())
        }
    }, [dispatch, isSignedIn]);

    // if there is no job ID in the url AND there is a last selected job
    // navigate to the last selected job and reset it
    useEffect(() => {
        if(!jobID && lastSelectedJob) {
            const loadedJob = lastSelectedJob
            dispatch(resetLastSelectedJob())
            navigate(`${loadedJob}`)
        }

    }, [jobID, lastSelectedJob, dispatch])

    // Check if the URL parameter exists,
    if (!jobID) {
        // If not, return a empty card.
        return (
            <>
                <Container className="bg-dark-subtle bg-opacity-25  rounded-5 border-shadow">
                    <Row>
                        <Card>
                            <Card.Body>
                                <Card.Title>
                                    <b>Choose a job to view more.</b>
                                </Card.Title>
                            </Card.Body>
                        </Card>
                    </Row>
                </Container>
            </>
        );
    }

    // Check if the the URL parameter exists in the jobLookup hashmap
    if (!jobLookup.has(jobID)) {
        // If not, return a card that says the URL is not valid.
        return (
            <>
                <Container className="bg-dark-subtle bg-opacity-25 rounded-5 border-shadow">
                    <Row>
                        <Card>
                            <Card.Body>
                                <Card.Title>
                                    <b role="status">
                                        Not a valid job, Choose a job to view more.
                                    </b>
                                </Card.Title>
                                <Row>
                                    <Col>
                                        {mobileView && (
                                            <Row ref={ref}>
                                                <Col
                                                    className="py-1"
                                                    xs={9}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "left"
                                                    }}
                                                >
                                                    <p>
                                                        View all listed positions:{" "}
                                                    </p>
                                                </Col>
                                                <Col
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "right"
                                                    }}
                                                >
                                                    <Link
                                                        style={{
                                                            textDecoration: "none"
                                                        }}
                                                        to={"/jobs/"}
                                                    >
                                                        <Button 
                                                            variant="primary"
                                                            aria-label="Back to Job Search page">
                                                            Back
                                                        </Button>
                                                    </Link>
                                                </Col>
                                            </Row>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Row>
                </Container>
            </>
        );
    }

    // Extract the Job information based on the given URL.
    const {
        positionTitle,
        // link,
        reqID,
        positionNumber,
        payRate,
        closingDate,
        jobAdditionalRequirements,
        jobDutiesResponsibilities,
        jobSkillSet,
        jobMinimumQualification,
        jobSpecialRequirements,
        jobEmpoymentType,
        jobWorkEnv,
        jobWorkDays,
        jobWorkHours,
        jobSensitivity,
        location,
        department,
        dateOpen,
        positionInHouse,
        rate
    } = jobLookup.get(jobID);


    // After checking if the jobID does NOT exist
    // AND if the job ID is in the req lookup
    // add it to the last selected job
    dispatch(setLastSelectedJob(reqID))

    // Async function that uses async local storage
    const asyncTempFunction = async () => {
        await asyncLocalStorage.setItem("storedJob", JSON.stringify({
            requisitionID: reqID,
            positionNumber:
                positionNumber,
            positionTitle:
                positionTitle,
            closingDate: closingDate,
            location: location,
            workHours: jobWorkHours,
            workDays: jobWorkDays,
            payRate: payRate,
            employmentType:
                jobEmpoymentType,
            sensitive: jobSensitivity,
            inHouse: positionInHouse,
            department: department,
            rate: rate
        }))
    }
    asyncTempFunction()
    
    

    // dispatch(
    //     setJobData({
    //         requisitionID: reqID,
    //         positionNumber:
    //             positionNumber,
    //         positionTitle:
    //             positionTitle,
    //         closingDate: closingDate,
    //         location: location,
    //         workHours: jobWorkHours,
    //         workDays: jobWorkDays,
    //         payRate: payRate,
    //         employmentType:
    //             jobEmpoymentType,
    //         sensitive: jobSensitivity,
    //         department: department
    //     })
    // );

    // Copy job to clipboard
    const handleClick = async () => {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
    };
    // Return a Card filled with the job information.
    return (
        <>
            <p
                autoFocus
                className="visually-hidden"
                >
                {
                    "Currently viewing " + positionTitle + " position ,"
                    + "Location: " + location + ","
                    + "Requisition ID: " + reqID + ","
                    + "Position ID: " + positionNumber + ","
                    + "Pay rate: " + payRate + ","
                    + "Closing Date:" + closingDate + ","
                    + "Work days:" + jobWorkDays + ","
                    + "Work Hours:" + jobWorkHours + ","
                    + "Sensitive:" + jobSensitivity + ","
                    + "Employment:" + jobEmpoymentType + ","
                    + "Job Duties and Responsibilities" + jobDutiesResponsibilities + ","
                    + "Additional Requirements:" + jobAdditionalRequirements + ","
                    + "Special Knowledge, Skills, and Abilities" + jobSkillSet + ","
                    + "Minimum Qualifications:" + jobMinimumQualification + ","
                    + "Special Knowledge, Skills, and Abilities:" + jobSpecialRequirements + ","
                    + "Work Environment:" + jobWorkEnv + ","
                    + "Date Open:" + dateOpen + ","
                    + "Rate: " + rate + ","

                }
                
                <p>
                    Key terms:                
                    O U F, Open Until Filled.
                    Sensitive, Sensitive Positions will be subjected to
                    background check.
                </p>
            </p>
            <Row>

                <Card
                    ref={ref}
                    >
                    <Card.Body
                        >
                        <Card.Title aria-hidden="true">
                            <Col
                                // xs={6}
                                className="d-flex justify-content-start mb-3 border-bottom border-warning border-5"
                            >
                                <b>{positionTitle}{positionInHouse === "Yes" && " (In-house)"}</b>
                            </Col>
                            <Col
                                xs={6}
                                className="d-flex justify-content-start"
                            >
                                <p>View Job Information as <a className="list-link" target="_blank" rel="noopener noreferrer" href={`https://www.dpm.navajo-nsn.gov/jva/${reqID}.pdf`}>PDF</a></p>
                            </Col>
                            
                            <Col
                                xs={6}
                                className="d-flex justify-content-start"
                                aria-hidden="true"
                            >
                                {!copied && (
                                    <Row xs={12} onClick={handleClick}>
                                        <Col xs={1} className="text-primary">
                                            <FaShareFromSquare />
                                        </Col>
                                        <Col>
                                            <h5 className="text-primary">Share Job</h5>
                                        </Col>
                                    </Row>
                                )}

                                {copied && (
                                    <Row xs={12} onClick={handleClick}>
                                        <Col xs={1}>
                                            <LuCopyCheck
                                                color="green"
                                                onClick={handleClick}
                                            />
                                        </Col>

                                        <Col>
                                            <h5 className="text-success">
                                                Copied to clipboard
                                            </h5>
                                        </Col>
                                    </Row>
                                )}

                            </Col>
                        </Card.Title>
                        <Row aria-hidden="true">
                            <Col>
                                <b >Location:</b> {location}
                            </Col>
                        
                        </Row>
                        <Card.Body aria-hidden="true">
                            <Row className="mb-1">
                                <Col>
                                    <b>Position ID:</b> {positionNumber}
                                </Col>

                                <Col>
                                    <b>Req ID: </b>
                                    {reqID}
                                </Col>
                                 <Col
                                    className="visually-hidden">
                                    {positionTitle + " Requisition ID: " + reqID}
                                </Col>
                            </Row>
                            <Row className="mb-1">
                                <Col
                                    aria-hidden="true">
                                    <b>Salary: </b>
                                    {payRate}
                                </Col>
                                 <Col
                                    className="visually-hidden">
                                    {positionTitle + " payrate: " + payRate}
                                </Col>

                                <Col
                                    aria-hidden="true">
                                    <b>Hourly Rate: </b>
                                    {rate ? `$${parseFloat(rate).toFixed(2)}` : ""}
                                </Col>
                                 <Col
                                    className="visually-hidden">
                                    {positionTitle + " Closing date: " + closingDate}
                                </Col>


                            </Row>
                            <Row className="mb-1">
                                <Col
                                    aria-hidden="true">
                                    <b>Work Days: </b>
                                    {jobWorkDays}
                                </Col>
                                 <Col
                                    className="visually-hidden">
                                    {positionTitle + " work days: " + jobWorkDays}
                                </Col>

                                <Col
                                    aria-hidden="true">
                                    <b>Work Hours: </b>
                                    {jobWorkHours}
                                </Col>
                                <Col
                                    className="visually-hidden">
                                    {positionTitle + " work hours: " + jobWorkHours}
                                </Col>
                            </Row>
                            <Row className="mb-1">
                                <Col
                                    aria-hidden="true">
                                    <b>Employment Type: </b>
                                    {jobEmpoymentType}
                                </Col>
                                <Col
                                    className="visually-hidden">
                                    {positionTitle + " employment type: " + jobEmpoymentType}
                                </Col>
                                <Col
                                    aria-hidden="true">
                                    <b>Sensitive: </b>
                                    {jobSensitivity}
                                </Col>
                                <Col
                                    className="visually-hidden">
                                    {positionTitle + " sensitive: " + jobSensitivity}
                                </Col>
                            </Row>

                            <Row className="mb-1">
                                <Col
                                    aria-hidden="true">
                                    <b>Date Open: </b>
                                    {dateOpen}
                                </Col>
                                <Col
                                    aria-hidden="true">
                                    <b>Closing Date: </b>
                                    {closingDate}
                                </Col>
                                <Col
                                    className="visually-hidden">
                                    {positionTitle + " date opening: " + dateOpen}
                                </Col>
                            </Row>
                            { positionInHouse === "Yes"
                                && (
                                    <Row className="mb-1">
                                        <Col
                                            aria-hidden="true">
                                            <b>In-House position: </b>
                                            {positionInHouse}
                                        </Col>
                                        <Col
                                            className="visually-hidden">
                                            {positionTitle + " In-House position: " + positionInHouse}
                                        </Col>
                                    </Row>
                                )
                            }
                        </Card.Body>
                        <Row aria-hidden="true" className="mb-1">
                            <Col>
                                <b className="border-bottom border-warning border-3">Job Duties and Responsibilities: </b>
                                <p>{jobDutiesResponsibilities}</p>
                            </Col>
                        </Row>
                        <Row aria-hidden="true" className="mb-1">
                            <Col>
                                <b className="border-bottom border-warning border-3">
                                    Special Knowledge, Skills, and Abilities:{" "}
                                </b>
                                <p>{jobSkillSet}</p>
                            </Col>
                        </Row>
                        <Row aria-hidden="true" className="mb-1">
                            {mobileView && (
                                <>
                                    <Row className="mb-1">
                                        <Col>
                                            <b>Minimum Qualifications: </b>
                                            <p>{jobMinimumQualification}</p>
                                        </Col>
                                    </Row>
                                    <Row className="mb-1">
                                        <Col
                                            aria-hidden="true">
                                            <b>Special Requirements: </b>
                                            <p>{jobSpecialRequirements}</p>
                                        </Col>
                                    </Row>
                                    <Row className="mb-1">
                                        <Col
                                            aria-hidden="true">
                                            <b>Additional Requirements: </b>
                                            <p>{jobAdditionalRequirements}</p>
                                        </Col>
                                    </Row>
                                </>
                            )}
                            {!mobileView && (
                                <>
                                    <Row className="mb-1">
                                        <Col>
                                            <b>Minimum Qualifications: </b>
                                            <p>{jobMinimumQualification}</p>
                                        </Col>
                                        
                                        <Col>
                                            <b>Special Requirements: </b>
                                            <p>{jobSpecialRequirements}</p>
                                            <b>Additional Requirements: </b>
                                            <p>{jobAdditionalRequirements}</p>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Row>
                        <Row aria-hidden="true">
                            <Col>
                                <b>Work Environment: </b>
                                <p>{jobWorkEnv}</p>
                            </Col>
                            
                        </Row>
                        <Row aria-hidden="true" className="fluid">
                            <Card.Footer>
                                <Row>
                                    <Col>
                                        <b className="border-bottom border-warning border-3">Key Terms:</b>
                                    </Col>
                                    <Row>
                                        <Col><strong>OUF:</strong> Open Until Filled</Col>
                                    </Row>
                                    <Row>
                                        <Col><strong>Sensitive:</strong> Sensitive Positions will be subjected to
                                        background check</Col>
                                    </Row>
                                </Row>
                            </Card.Footer>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                {isSignedIn && (
                                    <Button
                                        variant="primary"
                                        aria-describedby={"Apply for " + positionTitle + " position"}
                                        onClick={() => {
                                            navigate("/profileCard");
                                        }}
                                    >
                                        Apply Now
                                    </Button>

                                )}
                                {!isSignedIn && (
                                    <Button
                                        variant="primary"
                                        aria-describedby={"Sign in to Apply for " + positionTitle + " position"}
                                        onClick={() => {
                                            localStorage.setItem("storedJob", JSON.stringify({
                                                requisitionID: reqID,
                                                positionNumber:
                                                    positionNumber,
                                                positionTitle:
                                                    positionTitle,
                                                closingDate: closingDate,
                                                location: location,
                                                workHours: jobWorkHours,
                                                workDays: jobWorkDays,
                                                payRate: payRate,
                                                employmentType:
                                                    jobEmpoymentType,
                                                sensitive: jobSensitivity,
                                                department: department,
                                                inHouse: positionInHouse
                                            })
                                        )

                                            // dispatch(
                                            //     setJobData({
                                            //         requisitionID: reqID,
                                            //         positionNumber:
                                            //             positionNumber,
                                            //         positionTitle:
                                            //             positionTitle,
                                            //         closingDate: closingDate,
                                            //         location: location,
                                            //         workHours: jobWorkHours,
                                            //         workDays: jobWorkDays,
                                            //         payRate: payRate,
                                            //         employmentType:
                                            //             jobEmpoymentType,
                                            //         sensitive: jobSensitivity,
                                            //         department: department
                                            //     })
                                            // );
                                            
                                            navigate("/signin");
                                        }}
                                    >
                                        Sign-in to Apply
                                    </Button>
                                )}
                            </Col>
                                        <Row>

                                { positionInHouse === "Yes" &&
                                    (
                                        <p className="text-primary">* To be considered for this In-house position: you must be currently employed with the Navajo Nation and fill out your AB Number, Employment Status and Position Title in Profile.</p>
                                    )
                                }
                                </Row>
                            {mobileView && (
                                <>
                                    <Col
                                        className="job-detail-back-button"
                                        style={{
                                            display: "flex",
                                            justifyContent: "right"
                                        }}
                                        xs={5}
                                        aria-describedby="Go back to Jobs page button"
                                    >
                                        <Link
                                            style={{ textDecoration: "none" }}
                                            to={"/jobs/"}
                                            onClick={goBack}
                                        >
                                            <IoMdArrowRoundBack size={30} />
                                            Back
                                        </Link>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Card.Body>
                </Card>
            </Row>
        </>
    );
};
